import React from "react";
import { X, AlertCircle, FileSpreadsheet } from "lucide-react";
import { IBatchErrorResponse } from "../../../types/Response/AdminResponse";


interface BatchErrorsModalProps {
    isOpen: boolean;
    modalData: IBatchErrorResponse;
    onClose: () => void;
}

const BatchErrorsModal: React.FC<BatchErrorsModalProps> = ({ isOpen, modalData, onClose }) => {
    if (!isOpen) return null;

    const hasErrors = modalData?.errors && modalData.errors.length > 0;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white w-full max-w-3xl rounded-2xl shadow-xl flex flex-col max-h-[85vh] overflow-hidden">
                
                {/* MODAL HEADER */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-amber-50">
                    <div className="flex items-center gap-2 text-amber-800">
                        <AlertCircle className="w-5 h-5 text-amber-600" />
                        <div>
                            <h2 className="font-bold text-lg">Excel Content Diagnostics</h2>
                            <p className="text-xs text-amber-700/80">Batch ID: {modalData.batchId}</p>
                        </div>
                    </div>
                    <button 
                        onClick={onClose}
                        className="p-1 rounded-full text-gray-400 hover:bg-black/5 hover:text-gray-700 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* MODAL CONTENT */}
                <div className="p-6 overflow-y-auto flex-1">
                    {!hasErrors ? (
                        <div className="py-12 text-center">
                            <div className="w-12 h-12 rounded-full bg-green-50 text-green-600 flex items-center justify-center mx-auto mb-3">
                                <FileSpreadsheet className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900">Zero Structure Failures!</h3>
                            <p className="text-sm text-gray-500 max-w-sm mx-auto mt-1">
                                This excel batch passed parsing cleanly. You are safe to publish this data stack.
                            </p>
                        </div>
                    ) : (
                        <div>
                            <div className="mb-4 bg-red-50 border border-red-100 rounded-lg p-3 text-sm text-red-800 font-medium">
                                Found {modalData.errors.length} issue(s) that require attention before production data mapping.
                            </div>

                            {/* ERROR TABLE CONTAINER */}
                            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200 text-xs font-bold text-gray-600 uppercase tracking-wider">
                                            <th className="px-4 py-3">Sheet Name</th>
                                            <th className="px-4 py-3 w-28">Row Index</th>
                                            <th className="px-4 py-3">Error Diagnosis</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 text-sm">
                                        {modalData.errors.map((err, index) => (
                                            <tr key={index} className="hover:bg-gray-50/70 transition-colors">
                                                <td className="px-4 py-3 font-semibold text-gray-700">
                                                    {err.sheetName || "Default / Sheet1"}
                                                </td>
                                                <td className="px-4 py-3 text-red-600 font-mono font-bold">
                                                    #{err.excelRowNumber}
                                                </td>
                                                <td className="px-4 py-3 text-gray-600 leading-relaxed">
                                                    {err.errorMessage}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                {/* MODAL FOOTER */}
                <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-5 py-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                        Dismiss Window
                    </button>
                </div>

            </div>
        </div>
    );
};

export default BatchErrorsModal;